"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { enUS, fr } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

const localeMap = { en: enUS, fr } as const;

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/** react-day-picker v9 uses different class keys (month_caption, weekdays, button_previous, etc.) */
function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];
  const locale = localeMap[lang as keyof typeof localeMap] ?? enUS;

  return (
    <DayPicker
      locale={locale}
      showOutsideDays={showOutsideDays}
      navLayout="around"
      className={cn("p-3", className)}
      classNames={{
        root: "rdp-root relative",
        months: "flex flex-col sm:flex-row sm:space-x-4 sm:space-y-0",
        month: "relative grid grid-cols-[auto_1fr_auto] grid-rows-[auto_1fr] gap-x-2 gap-y-2 items-center",
        month_caption: "col-start-2 row-start-1 flex justify-center items-center h-9 min-h-9",
        caption_label: "text-sm font-medium",
        nav: "hidden",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "col-start-1 row-start-1 h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 cursor-pointer place-self-center"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "col-start-3 row-start-1 h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 cursor-pointer place-self-center"
        ),
        month_grid: "col-start-1 col-end-4 row-start-2 w-full border-collapse",
        weekdays: "",
        weekday: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] p-1",
        weeks: "",
        week: "",
        day: "h-9 w-9 text-center text-sm p-0 relative align-middle [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100 cursor-pointer"
        ),
        range_end: "day-range-end",
        selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        today: "bg-accent text-accent-foreground",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
        range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
